import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Take manual control of scroll restoration at module load — before any
// component renders — so the browser's native behaviour cannot interfere.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/**
 * Preserves the visitor's scroll position within a product catalogue across
 * back-navigation.
 *
 *   • Call `saveNow()` from a click handler on a product link. This snapshots
 *     `window.scrollY` into sessionStorage under a key that includes the
 *     current pathname + search so each filter state has its own memory.
 *   • On mount, when `ready` becomes truthy, we read that saved position
 *     and scroll to it — using a short retry loop while the grid grows to
 *     its final height. Restoration runs once per mount.
 *   • sessionStorage is per-tab, so a fresh tab (e.g. search-engine landing)
 *     lands at the top as expected.
 *
 * @param {boolean} ready  Truthy once the page has enough content laid out.
 * @returns {{ saveNow: () => void }}  Call `saveNow()` from `onClick` on
 *          each product link so the scroll is captured at the exact moment
 *          of navigation.
 */
export default function useCatalogueScrollRestore(ready) {
  const { pathname, search } = useLocation();
  const key = `catalogue-scroll:${pathname}${search}`;
  const restored = useRef(false);
  const keyRef = useRef(key);
  keyRef.current = key;

  useEffect(() => {
    if (restored.current || !ready) return;
    restored.current = true;

    let saved;
    try { saved = sessionStorage.getItem(key); } catch { return; }
    if (saved == null) return;
    const targetY = parseInt(saved, 10);
    if (!Number.isFinite(targetY) || targetY <= 0) return;

    // Clear now so a subsequent forward-nav (not a return) starts at top.
    try { sessionStorage.removeItem(key); } catch { /* ignore */ }

    let attempts = 0;
    const maxAttempts = 30;
    const step = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= targetY - 4 || attempts >= maxAttempts) {
        window.scrollTo(0, Math.min(targetY, Math.max(0, maxScroll)));
        return;
      }
      attempts += 1;
      setTimeout(step, 40);
    };
    requestAnimationFrame(step);
  }, [key, ready]);

  return {
    saveNow: () => {
      try {
        const y = window.scrollY;
        if (y > 0) sessionStorage.setItem(keyRef.current, String(y));
      } catch { /* ignore */ }
    },
  };
}
